import {
  OrderInternalDocumentViewModel,
  OrderInternalFormFieldViewModel,
  OrderSuppliersDocumentViewModel,
  OrderSuppliersFormFieldViewModel,
  WorkflowOrderKind,
} from '@ail/orders-api'
import { WorkflowStatus } from '@ail/workflows-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../auth/UseAuth'
import { downloadFile } from '../../../helpers/FileDownload'
import { printDate } from '../../../helpers/Utils'
import { useSettings } from '../../../hooks/useSettings'
import {
  useGetOrderInternalAuditQuery,
  useGetOrderQuery,
  useGetOrderSuppliersAuditQuery,
  useLazyGetOrderInternalDocumentQuery,
  useLazyGetOrderSuppliersDocumentQuery,
} from '../../../redux/api/ordersApi'
import { Dialog } from '../../common/Dialog/Dialog'
import { PopupType } from '../../common/Popup/Popup'
import { usePopup } from '../../common/Popup/PopupContext'
import { Slider } from '../../common/Slider/Slider'
import { DocPreviewData, Document as OrderDocument } from '../DocumentsModal/DocumentsModal'
import { Order } from '../Orders'
import { WorkflowPreviewModal } from '../WorkflowPreviewModal/WorkflowPreviewModal'
import './DetailsModal.scss'

type FormField = OrderInternalFormFieldViewModel | OrderSuppliersFormFieldViewModel

interface DetailsModalProps {
  order?: Order
  workflowStatuses?: Partial<Record<string, WorkflowStatus>>
  onClose: () => void
}

export function DetailsModal(props: DetailsModalProps) {
  const [expanded, setExpanded] = useState(true)
  const [page, setPage] = useState(0)
  const [docToPreview, setDocToPreview] = useState<DocPreviewData>()
  const { user } = useAuth()
  const [t, i18n] = useTranslation()
  const [settings, updateSettings] = useSettings('supplierOrderDetails')

  const loadInternal = !!props.order && !props.order.tenantId
  const loadSuppliers = !!props.order && !!props.order.tenantId
  const loadInternalAudit = !!user?.isInternalUser && loadInternal
  const loadSuppliersAudit = !!user?.isInternalUser && loadSuppliers
  const loadRequest = !!user?.isInternalUser && !!props.order
  const request = useGetOrderQuery(loadRequest ? props.order!.orderId : skipToken)
  const internalAudit = useGetOrderInternalAuditQuery(loadInternalAudit ? props.order!.id : skipToken)
  const suppliersAudit = useGetOrderSuppliersAuditQuery(loadSuppliersAudit ? props.order!.id : skipToken)
  const [downloadInternal] = useLazyGetOrderInternalDocumentQuery()
  const [downloadSuppliers] = useLazyGetOrderSuppliersDocumentQuery()
  const audit = props.order?.tenantId ? suppliersAudit.data : internalAudit.data
  const pageCount = 1 + (audit?.length ?? 0)
  const current = page === 0 ? props.order : audit?.[page - 1]
  const popup = usePopup()

  const switchPage = (value: number) => {
    const newPage = page + value
    if (newPage < 0 || newPage >= pageCount) {
      return
    }
    setPage(newPage)
  }

  useEffect(() => {
    setPage(0)
  }, [props.order])

  const copyValue = (value?: string) => {
    if (!value) {
      return
    }
    navigator.clipboard.writeText(value)
    popup?.pushPopup('Value has been copied', PopupType.Success)
  }

  const formatDate = (value?: string) => {
    if (value && /(\d{2})\.(\d{2})\.(\d{4})/.test(value) === false) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('fr-CH')
      }
    }
    return value
  }

  const parseFileData = (value?: string) => {
    if (value) {
      const fileArr = value.split('|')
      const fileURL = fileArr[1]
      const filename = fileArr[0]
      if (fileURL && filename) {
        return { fileURL, filename }
      }
    }
    return undefined
  }

  const download = async (documentId: string) => {
    if (!props.order) {
      return
    }

    try {
      let result: OrderInternalDocumentViewModel | OrderSuppliersDocumentViewModel | undefined = undefined
      if (props.order.tenantId) {
        result = await downloadSuppliers({ id: props.order.id, documentId }).unwrap()
      } else {
        result = await downloadInternal({ id: props.order.id, documentId }).unwrap()
      }

      const fileData = parseFileData(result.content)
      if (fileData) {
        downloadFile(fileData)
      }
    } catch (e) {
      console.error(e)
    }
  }

  // A document with a configured workflow can be started, or started again once terminated.
  const canStartWorkflow = (document: OrderDocument) => {
    if (page > 0 || !user?.isInternalUser || !document.workflowKind || !document.documentId) {
      return false
    }
    return !document.workflowId || props.workflowStatuses?.[document.workflowId] === WorkflowStatus.Terminated
  }

  const workflowLabel = (document: OrderDocument) => {
    if (!document.workflowId) {
      return document.workflowKind && document.documentId ? t('WORKFLOWCANSTART') : undefined
    }
    const status = props.workflowStatuses?.[document.workflowId]
    // The workflow exists, but its status could not be fetched (workflows api unresponsive).
    return status ? t(status.toUpperCase()) : t('WORKFLOWUNAVAILABLE')
  }

  const renderUploadRow = (name: string, label: string, field: FormField) => {
    const documents: (OrderInternalDocumentViewModel | OrderSuppliersDocumentViewModel)[] = field.documents
    const anyUploaded = documents.some((d) => d.documentId)
    if (!settings.showEmptyFields && !anyUploaded) {
      return null
    }
    return (
      <tr key={`${name}_${label}_upload`} className={anyUploaded ? '' : 'empty-field'}>
        <td>{label}</td>
        <td>
          <div className="details-documents">
            {!documents.length && <div className="details-field-value">-</div>}
            {documents.map((document) => {
              const documentName =
                document.tr?.[i18n.language] ??
                (documents.length === 1 ? (field.tr?.[i18n.language] ?? field.name) : document.name)
              const status = workflowLabel(document)
              return (
                <div key={document.name} className="details-field details-document">
                  <i className={document.documentId ? 'icon-check' : 'icon-check disabled'} />
                  <div className="details-field-value" title={documentName}>
                    <span className="details-document-name">{documentName}</span>
                    {document.documentId ? (
                      <a
                        className="details-document-file link"
                        title={document.fileName}
                        onClick={() => download(document.documentId!)}
                      >
                        {document.fileName}
                      </a>
                    ) : (
                      <span className="details-document-file">{document.fileName ?? t('NO_DOCUMENTS_FOUND')}</span>
                    )}
                  </div>
                  {!!status && <span className="details-document-workflow">{status}</span>}
                  <div className="details-field-actions">
                    {canStartWorkflow(document) && (
                      <button type="button" onClick={() => setDocToPreview({ document, order: props.order!, field })}>
                        {t(document.workflowId ? 'RESTART_WORKFLOW' : 'START_WORKFLOW')}
                      </button>
                    )}
                    {!!document.fileName && <i className="icon-copy" onClick={() => copyValue(document.fileName)} />}
                  </div>
                </div>
              )
            })}
          </div>
        </td>
      </tr>
    )
  }

  const renderDetailsRow = (name: string, label: string, value?: string, field?: FormField) => {
    const type = field?.type ?? 'text'
    if (type === 'upload' && field) {
      return renderUploadRow(name, label, field)
    }
    const bindingSource = field?.bindingSource ?? ''
    const bind = field && props.order?._fields[field.name]?.bind
    let actualValue = value
    if (bindingSource && request.data?.data[bindingSource] != null && page > 0 && bind) {
      actualValue = request.data.data[bindingSource]
    }
    let formattedValue = actualValue
    if (type === 'boolean') {
      const parts = formattedValue?.split('|') ?? []
      if (parts.length >= 2 && parts[1].startsWith('data:')) {
        formattedValue = parts[0]
      }
    } else if (type === 'date') {
      formattedValue = formatDate(actualValue)
    }
    if (settings.showEmptyFields || (!settings.showEmptyFields && actualValue)) {
      return (
        <tr key={`${name}_${label}_${type}`} className={actualValue ? '' : 'empty-field'}>
          <td>{label}</td>
          <td>
            <div className="details-field">
              <div className="details-field-value">{formattedValue}</div>
              <div className="details-field-actions">
                {!!actualValue && <i className="icon-copy" onClick={() => copyValue(formattedValue)} />}
              </div>
            </div>
          </td>
        </tr>
      )
    } else {
      return null
    }
  }

  if (docToPreview) {
    return (
      <WorkflowPreviewModal
        data={docToPreview}
        onClose={() => setDocToPreview(undefined)}
        onCancel={() => setDocToPreview(undefined)}
      />
    )
  }

  return (
    <Dialog open={!!props.order} onClose={props.onClose} width={800}>
      <div className="details-modal">
        <div className="details-modal-header">
          <div className="details-modal-header-left">
            <div className="btn" onClick={props.onClose}>
              <i className="icon-left"></i> BACK
            </div>
            <span>Details</span>
          </div>
          <Slider
            label={t('EMPTY_FIELDS')}
            labelPosition={'before'}
            checked={settings.showEmptyFields}
            onChange={() =>
              updateSettings({
                showEmptyFields: !settings.showEmptyFields,
              })
            }
          ></Slider>
          {pageCount > 1 && (
            <div className="details-modal-header-right">
              <div className="btn" onClick={() => switchPage(-1)}>
                <i className="icon-left" />
              </div>
              <span>
                {page + 1} / {pageCount}
              </span>
              <div className="btn" onClick={() => switchPage(1)}>
                <i className="icon-right" />
              </div>
            </div>
          )}
        </div>
        <div className="details-modal-body">
          <table>
            <colgroup>
              <col style={{ width: 150 }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th colSpan={2}>
                  <span>Details</span>
                  <i className={expanded ? 'icon-bottom' : 'icon-up'} onClick={() => setExpanded(!expanded)}></i>
                </th>
              </tr>
            </thead>
            {expanded && (
              <tbody>
                {(props.order?.kind === WorkflowOrderKind.Outgoing || user?.isInternalUser) &&
                  renderDetailsRow(
                    'logisticId',
                    props.order?.kind === WorkflowOrderKind.Outgoing ? t('LOGISTICID')! : t('REPLACEID')!,
                    props.order?.logisticId
                  )}
                {renderDetailsRow(
                  'returnLogisticId',
                  props.order?.kind === WorkflowOrderKind.Incoming ? t('LOGISTICID')! : t('REPLACEID')!,
                  props.order?.returnLogisticId
                )}
                {current?.form.fields.map((field) =>
                  renderDetailsRow(
                    field.name,
                    field.tr?.[i18n.language] ?? field.tr?.['en'] ?? field.name,
                    field.value,
                    field
                  )
                )}
                {renderDetailsRow('companyName', t('COMPANY')!, props.order?.currentCompanyName)}
                {renderDetailsRow('costCenterName', t('COST_CENTER')!, props.order?.currentCostCenterName)}
                {renderDetailsRow('invoiceAddress', t('INVOICE_ADDRESS')!, props.order?.invoiceAddress)}
                {renderDetailsRow('contractReference', t('REFERENCE')!, props.order?.contractReference)}
                {renderDetailsRow('createdAt', t('CREATED')!, printDate(props.order?.createdAt, true))}
                {renderDetailsRow(
                  'updatedAt',
                  t('MODIFIED')!,
                  `${printDate(current?.updatedAt, true)} ${current?.modifiedBy ? `(${current?.modifiedBy})` : ``}`
                )}
                {renderDetailsRow('status', t('STATUS')!, current?.form.status)}
                {renderDetailsRow('id', 'ID', current?.id)}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </Dialog>
  )
}
